import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Search, Plus, X, Crown, CheckSquare, Square } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGlobalTheme } from '../../hooks/useGlobalTheme';
import { isSaudiBrand } from '../../../products/saudi/utils/isSaudiBrand';
import { Z_INDEX } from '../../constants/zIndexLayers';
import './CustomComponents.css';

/** Option-like objects from CustomSelect / measure units: { value, label, ... } */
function coerceSelectValueKey(raw) {
  if (raw == null || raw === '') return raw;
  if (typeof raw === 'object' && raw !== null && Object.prototype.hasOwnProperty.call(raw, 'value')) {
    return raw.value;
  }
  return raw;
}

function stringFromOptionLike(raw) {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'object' && raw !== null) {
    const piece = raw.displayValue ?? raw.label ?? raw.value;
    return piece != null ? String(piece) : '';
  }
  return String(raw);
}

/** Normalise value prop when `multiple` — array, pipe string, or scalar. */
function normalizeMultiSelectValues(raw, separator = '|') {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((v) => String(coerceSelectValueKey(v) ?? v).trim()).filter(Boolean);
  }
  const s = String(raw).trim();
  if (!s) return [];
  if (s.includes(separator)) {
    return s.split(separator).map((p) => p.trim()).filter(Boolean);
  }
  return [s];
}

function stripAccentsLower(s) {
  return String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

/** Associe saisie libre à une option existante (ex. « défectueux » → value `defectueux`). */
function resolveMultiPickValue(raw, options) {
  const trimmed = raw != null ? String(raw).trim() : '';
  if (!trimmed) return '';

  const lower = trimmed.toLowerCase();
  const accentLower = stripAccentsLower(trimmed);

  for (const o of options) {
    if (o?.value != null && String(o.value).toLowerCase() === lower) return o.value;
  }
  for (const o of options) {
    if (o?.label && o.label.toLowerCase() === lower) return o.value;
  }
  for (const o of options) {
    if (o?.displayValue && o.displayValue.toLowerCase() === lower) return o.value;
  }
  for (const o of options) {
    if (o?.value != null && stripAccentsLower(o.value) === accentLower) return o.value;
  }
  for (const o of options) {
    if (o?.label && stripAccentsLower(o.label) === accentLower) return o.value;
  }

  const partial = options.filter(
    (o) => o?.label && stripAccentsLower(o.label).includes(accentLower) && accentLower.length >= 3,
  );
  if (partial.length === 1) return partial[0].value;

  return trimmed;
}

function normalizeMultiSelection(values, options) {
  const out = [];
  for (const v of values) {
    const resolved = resolveMultiPickValue(v, options);
    if (!resolved) continue;
    const key = String(resolved);
    if (!out.some((x) => String(x) === key)) out.push(resolved);
  }
  return out;
}

function filterTermMatchesKnownOption(term, options) {
  const trimmed = String(term ?? '').trim();
  if (!trimmed) return false;

  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return false;
    return parts.every((part) => {
      const resolved = resolveMultiPickValue(part, options);
      return options.some((o) => o?.value != null && String(o.value) === String(resolved));
    });
  }

  const resolved = resolveMultiPickValue(trimmed, options);
  return options.some((o) => o?.value != null && String(o.value) === String(resolved));
}

/** Saisie en cours après les libellés déjà sélectionnés (ex. « A, B, E » → « E »). */
function extractMultiTypingSegment(inputStr, labelsText) {
  const input = String(inputStr ?? '');
  const labels = String(labelsText ?? '').trim();
  if (!labels) return input.trim();

  if (input.length >= labels.length && input.slice(0, labels.length) === labels) {
    return input.slice(labels.length).replace(/^[\s,]+/, '').trim();
  }

  return input.trim();
}

function labelForSelectValue(val, options) {
  const opt = options.find((o) => o != null && String(o.value) === String(val));
  return opt ? String(opt.displayValue || opt.label || opt.value) : String(val);
}

/** Même icônes que les coches signatures (Bon pour / Établi par / Reçue par). */
function CustomSelectMultiCheck({ checked, color = '#3b82f6', size = 20, isLight = false }) {
  if (checked) {
    return <CheckSquare size={size} style={{ color }} className="flex-shrink-0" aria-hidden />;
  }
  const uncheckedColor = isLight ? '#64748b' : '#ffffff';
  return <Square size={size} style={{ color: uncheckedColor }} className="flex-shrink-0" aria-hidden />;
}

/**
 * CustomSelect - Styled dropdown / combobox (creatable + searchable).
 * Theme-aware: **light** (dashboard clair) vs **dark** (_GPS glass_).
 * 
 * @param {Object} props
 * @param {string} props.value - Selected value
 * @param {Function} props.onChange - Callback when selection changes
 * @param {Array} props.options - Array of { value, label, icon?, isDefault?, disabled?, premium? } — disabled skips selection; premium shows Crown + badge
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Disable the select
 * @param {boolean} props.searchable - Enable search functionality
 * @param {boolean} props.creatable - Allow typing custom values (3-in-1 combobox). If omitted, `allowCreate` is used; default true.
 * @param {boolean} props.allowCreate - Legacy alias: same as creatable (matches Contrats/Projets pages). Ignored if `creatable` is set.
 * @param {string} props.createLabel - Title for the green "create" row in the list (3-in-1)
 * @param {boolean} props.autoSelect - If false, typing does not call onChange until blur, Enter, or list pick (avoids "auto-filling" parent state)
 * @param {Function} props.onDeleteOption - Callback when option delete is clicked (receives option value)
 * @param {boolean} props.multiple - 4-in-1 : sélection multiple (conserve search / create / pick)
 * @param {string} props.multipleCheckColor - Couleur CheckSquare coché (défaut bleu signatures « Établi par »)
 * @param {string} props.valueSeparator - Séparateur si `value` est une chaîne (défaut « | »)
 * @param {string} props.autoComplete - autocomplete sur l'input (défaut "off", limite les alertes Chrome sur HTTP)
 * @param {string} props.suffix - Suffix text to display after the value (e.g., '%', 'DA')
 */
const CustomSelect = ({ 
  value, 
  onChange, 
  options = [], 
  placeholder: placeholderProp,
  className = '',
  disabled = false,
  searchable = true,
  creatable: creatableProp,
  allowCreate,
  createLabel: createLabelProp,
  autoSelect = true,
  onDeleteOption = null,
  autoComplete = 'off',
  suffix = '',
  multiple = false,
  multipleCheckColor = '#3b82f6',
  valueSeparator = '|',
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();
  const { isDashboardLightMode: isLight } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const placeholder = placeholderProp ?? t('common.select', 'اختر');
  const createLabel = createLabelProp ?? t('common.create_new_type', 'إنشاء نوع جديد');
  const searchPlaceholder = t('common.search', 'بحث');
  const noResultsText = t('common.no_results', 'لم يتم العثور على نتائج');
  const noOptionsText = t('common.no_options', 'لا توجد خيارات متاحة');
  const createNewPrefix = t('common.create_new_prefix', 'إنشاء جديد:');

  const valueKey = useMemo(() => coerceSelectValueKey(value), [value]);
  const selectedValues = useMemo(
    () =>
      multiple
        ? normalizeMultiSelection(normalizeMultiSelectValues(value, valueSeparator), options)
        : [],
    [multiple, value, valueSeparator, options],
  );

  const isCreatable =
    creatableProp !== undefined
      ? creatableProp
      : allowCreate !== undefined
        ? allowCreate
        : true;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  /** Multi + creatable : false = afficher les libellés sélectionnés ; true = saisie filtre / création. */
  const [multiFilterActive, setMultiFilterActive] = useState(false);
  const [inputValue, setInputValue] = useState(() =>
    value != null ? stringFromOptionLike(value) || String(valueKey ?? '') : ''
  );
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, openUp: false });

  const updateDropdownPosition = useCallback(() => {
    if (!selectRef.current) return;
    const rect = selectRef.current.getBoundingClientRect();
    const dropdownMaxHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

    setDropdownPosition({
      top: rect.bottom + 8,
      bottom: window.innerHeight - rect.top + 8,
      left: rect.left,
      width: Math.max(rect.width, 160),
      openUp,
    });
  }, []);

  const dropdownPortalStyle = useMemo(() => {
    const base = {
      position: 'fixed',
      left: dropdownPosition.left,
      width: dropdownPosition.width,
      zIndex: Z_INDEX.MODAL_DROPDOWN,
      ...(dropdownPosition.openUp
        ? { bottom: dropdownPosition.bottom, top: 'auto' }
        : { top: dropdownPosition.top, bottom: 'auto' }),
    };
    if (qawaem || isLight) {
      return {
        ...base,
        background: 'rgba(252, 254, 252, 0.97)',
        border: '1px solid rgba(110, 155, 110, 0.28)',
        boxShadow:
          '0 0 0 1px rgba(255, 255, 255, 0.6) inset, 0 12px 32px rgba(46, 69, 46, 0.14), 0 0 24px rgba(110, 155, 110, 0.08)',
      };
    }
    return {
      ...base,
      background: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
      border: '1px solid rgba(96, 165, 250, 0.6)',
      backdropFilter: 'blur(12px)',
      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)',
    };
  }, [qawaem, isLight, dropdownPosition.top, dropdownPosition.bottom, dropdownPosition.left, dropdownPosition.width, dropdownPosition.openUp]);

  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const inputRef = useRef(null);
  const inputValueRef = useRef(inputValue);
  /** True while user backspaces through comma-separated labels (not a short filter query). */
  const multiTrimmingLabelsRef = useRef(false);
  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  const multiLabelsText = useMemo(
    () =>
      selectedValues.length > 0
        ? selectedValues.map((v) => labelForSelectValue(v, options)).join(', ')
        : '',
    [selectedValues, options],
  );

  /** Motifs créés par l'utilisateur — absents de `options`, affichés seuls en tête de liste. */
  const customSelectedOnly = useMemo(
    () =>
      selectedValues.filter(
        (sv) => !options.some((o) => o?.value != null && String(o.value) === String(sv)),
      ),
    [selectedValues, options],
  );

  // Sync input value with prop value (creatable — single label or multi labels)
  useEffect(() => {
    if (!isCreatable) return;
    if (multiple) {
      if (multiFilterActive) return;
      setInputValue(multiLabelsText);
      return;
    }
    const matchedOption = options.find(
      (opt) => opt != null && String(opt.value) === String(valueKey)
    );
    const showOptionLabel =
      matchedOption &&
      valueKey !== '' &&
      valueKey != null &&
      matchedOption.value !== '' &&
      matchedOption.value != null;
    if (showOptionLabel) {
      setInputValue(String(matchedOption.displayValue || matchedOption.label || ''));
    } else if (value != null && value !== '') {
      const fromObj = stringFromOptionLike(value);
      setInputValue(fromObj || (valueKey != null ? String(valueKey) : ''));
    } else {
      setInputValue('');
    }
  }, [value, valueKey, isCreatable, options, multiple, multiLabelsText, multiFilterActive]);

  // Affichage des libellés sélectionnés ≠ mode filtre / création
  useEffect(() => {
    if (!multiple || !isCreatable || !multiLabelsText) return;
    const current =
      typeof inputValue === 'object' ? stringFromOptionLike(inputValue) : String(inputValue ?? '');
    if (current.trim() === multiLabelsText.trim() && multiFilterActive) {
      setMultiFilterActive(false);
      multiTrimmingLabelsRef.current = false;
    }
  }, [multiple, isCreatable, multiLabelsText, inputValue, multiFilterActive]);

  const commitCreatableValue = useCallback(() => {
    if (autoSelect || !isCreatable) return;
    const raw = inputValueRef.current;
    const trimmed = raw != null ? String(raw).trim() : '';
    if (trimmed === '') {
      onChange('');
      return;
    }
    const byValue = options.find(
      (o) => o != null && o.value != null && String(o.value).toLowerCase() === trimmed.toLowerCase()
    );
    if (byValue) {
      onChange(byValue.value);
      setInputValue(byValue.displayValue || byValue.label);
      return;
    }
    const byLabel = options.find(
      (o) => o && o.label && o.label.toLowerCase() === trimmed.toLowerCase()
    );
    if (byLabel) {
      onChange(byLabel.value);
      setInputValue(byLabel.displayValue || byLabel.label);
      return;
    }
    const byDisplayValue = options.find(
      (o) => o && o.displayValue && o.displayValue.toLowerCase() === trimmed.toLowerCase()
    );
    if (byDisplayValue) {
      onChange(byDisplayValue.value);
      setInputValue(byDisplayValue.displayValue);
      return;
    }
    onChange(trimmed);
  }, [autoSelect, isCreatable, onChange, options]);

  // Update dropdown position when opened / on scroll inside modals
  useEffect(() => {
    if (!isOpen) return undefined;
    updateDropdownPosition();
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen, updateDropdownPosition]);

  const restoreMultiInputLabels = useCallback(() => {
    setMultiFilterActive(false);
    multiTrimmingLabelsRef.current = false;
    setInputValue(multiLabelsText);
  }, [multiLabelsText]);

  const applyMultiLabelsFromValues = useCallback(
    (values) => {
      setMultiFilterActive(false);
      multiTrimmingLabelsRef.current = false;
      const labels =
        values.length > 0
          ? values.map((v) => labelForSelectValue(v, options)).join(', ')
          : '';
      setInputValue(labels);
    },
    [options],
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the select button AND the dropdown
      const isOutsideSelect = selectRef.current && !selectRef.current.contains(event.target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
      
      if (isOutsideSelect && isOutsideDropdown) {
        setIsOpen(false);
        setSearchTerm('');
        if (multiple && isCreatable) {
          restoreMultiInputLabels();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [multiple, isCreatable, restoreMultiInputLabels]);

  // Focus search input when dropdown opens (non-creatable only — creatable types in main field)
  useEffect(() => {
    if (isOpen && searchable && !isCreatable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable, isCreatable]);

  const selectedOption = options.find(
    (opt) => opt != null && String(opt.value) === String(valueKey)
  );
  const multiDisplayText =
    selectedValues.length > 0
      ? selectedValues.map((v) => labelForSelectValue(v, options)).join(', ')
      : placeholder;
  const displayText = multiple
    ? multiDisplayText
    : selectedOption
      ? selectedOption.label
      : value != null && value !== '' && typeof value === 'object'
        ? stringFromOptionLike(value) || placeholder
        : value != null && value !== ''
          ? String(valueKey)
          : placeholder;
  const displayIcon = multiple ? null : selectedOption?.icon;
  const displayColor = multiple ? null : selectedOption?.color;

  const inputValueStr = typeof inputValue === 'object' ? stringFromOptionLike(inputValue) : String(inputValue ?? '');
  const isShowingSelectedLabelDisplay =
    multiple &&
    Boolean(multiLabelsText) &&
    inputValueStr.trim() === multiLabelsText.trim();
  const multiTypingSegment = useMemo(() => {
    if (!multiple || !multiFilterActive || isShowingSelectedLabelDisplay) return '';
    const labels = multiLabelsText.trim();
    if (labels && inputValueStr.length >= labels.length && inputValueStr.slice(0, labels.length) === labels) {
      return extractMultiTypingSegment(inputValueStr, labels);
    }
    return inputValueStr.trim();
  }, [multiple, multiFilterActive, isShowingSelectedLabelDisplay, inputValueStr, multiLabelsText]);
  const filterTerm = multiple
    ? multiFilterActive && !isShowingSelectedLabelDisplay
      ? multiTypingSegment
      : ''
    : isCreatable
      ? inputValueStr
      : searchTerm;
  const isCreatingNew =
    isCreatable &&
    (!multiple || (isOpen && multiFilterActive && !isShowingSelectedLabelDisplay)) &&
    String(filterTerm).trim().length >= (multiple ? 3 : 1) &&
    !filterTermMatchesKnownOption(filterTerm, options);

  const filteredOptions =
    filterTerm && String(filterTerm).trim()
      ? options.filter((opt) =>
          opt != null &&
          String(opt.label ?? opt.value ?? '')
            .toLowerCase()
            .includes(String(filterTerm).toLowerCase()),
        )
      : options;

  const emitMultipleChange = useCallback(
    (nextArr) => {
      const clean = normalizeMultiSelection(
        Array.isArray(nextArr) ? nextArr : [],
        options,
      );
      onChange(clean);
    },
    [onChange, options],
  );

  const handleToggleMulti = (optionValue) => {
    const key = String(optionValue);
    const exists = selectedValues.some((v) => String(v) === key);
    const next = exists
      ? selectedValues.filter((v) => String(v) !== key)
      : normalizeMultiSelection([...selectedValues, optionValue], options);
    emitMultipleChange(next);
    applyMultiLabelsFromValues(next);
  };

  const getMultiCreatableRaw = useCallback(() => {
    const current = String(inputValueRef.current ?? inputValueStr ?? '');
    if (!multiple) return current.trim();
    const labels = multiLabelsText.trim();
    if (labels && current.length >= labels.length && current.slice(0, labels.length) === labels) {
      return extractMultiTypingSegment(current, labels);
    }
    return current.trim();
  }, [multiple, multiLabelsText, inputValueStr]);

  const handleAddMultiCustom = (raw) => {
    const trimmed = raw != null ? String(raw).trim() : '';
    if (!trimmed) return;
    const pick = resolveMultiPickValue(trimmed, options);
    if (selectedValues.some((v) => String(v) === String(pick))) return;
    const next = normalizeMultiSelection([...selectedValues, pick], options);
    emitMultipleChange(next);
    applyMultiLabelsFromValues(next);
  };

  const handleSelect = (optionValue) => {
    if (multiple) {
      handleToggleMulti(optionValue);
      return;
    }
    const opt = options.find(
      (o) => o != null && String(o.value) === String(optionValue)
    );
    if (opt?.disabled) return;
    onChange(optionValue);
    const selectedOpt = options.find(opt => opt.value === optionValue);
    if (selectedOpt && isCreatable) {
      setInputValue(selectedOpt.displayValue || selectedOpt.label);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const prevValue = inputValueStr;
    const labels = multiLabelsText.trim();

    if (multiple) {
      if (!isOpen) setIsOpen(true);

      if (newValue.trim() === '') {
        if (
          selectedValues.length > 0 &&
          (!multiFilterActive || multiTrimmingLabelsRef.current)
        ) {
          emitMultipleChange([]);
          setInputValue('');
          setMultiFilterActive(false);
          multiTrimmingLabelsRef.current = false;
          return;
        }

        setInputValue('');
        setMultiFilterActive(true);
        return;
      }

      const prevTrim = prevValue.trim();
      const replacedWithShortFilter =
        labels &&
        prevTrim === labels &&
        newValue.length < prevValue.length &&
        newValue.length <= 2 &&
        labels.length > 12;

      if (
        labels &&
        !replacedWithShortFilter &&
        newValue.length < prevValue.length &&
        (prevTrim === labels || prevValue.startsWith(newValue))
      ) {
        multiTrimmingLabelsRef.current = true;
      } else if (replacedWithShortFilter || newValue.length > prevValue.length) {
        multiTrimmingLabelsRef.current = false;
      }

      setInputValue(newValue);
      setMultiFilterActive(true);
      return;
    }

    setInputValue(newValue);
    if (autoSelect) {
      onChange(newValue);
    }
    if (!isOpen && newValue) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    if (!multiple && isCreatable) {
      inputRef.current?.select?.();
    }
  };

  const handleInputBlur = () => {
    if (multiple && isCreatable) {
      window.setTimeout(() => {
        const active = document.activeElement;
        if (dropdownRef.current?.contains(active) || selectRef.current?.contains(active)) {
          return;
        }
        const current = String(inputValueRef.current ?? '').trim();
        if (current === '' && selectedValues.length > 0) {
          restoreMultiInputLabels();
          return;
        }
        restoreMultiInputLabels();
      }, 120);
      return;
    }
    if (!autoSelect && isCreatable) {
      commitCreatableValue();
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && isCreatable) {
      if (multiple && isCreatingNew) {
        e.preventDefault();
        handleAddMultiCustom(getMultiCreatableRaw());
        return;
      }
      if (!autoSelect && !multiple) {
        e.preventDefault();
        commitCreatableValue();
        setIsOpen(false);
      }
    }
  };

  const useCreatableInput = isCreatable;

  return (
    <div
      ref={selectRef}
      data-testid={dataTestId}
      className={`relative min-w-0 w-full max-w-full box-border ${className}`}
    >
      {useCreatableInput ? (
        /* Creatable: Text Input - Type directly */
        <div className="relative min-w-0 w-full">
          <input
            ref={inputRef}
            type="text"
            value={inputValueStr}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            onFocus={handleInputFocus}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete={autoComplete}
            className={`
              w-full min-w-0 max-w-full box-border rounded-xl px-4 py-2.5 pr-10
              focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              ${
                isLight
                  ? 'bg-white border border-slate-300 text-slate-900 placeholder:text-slate-400'
                  : 'bg-white/10 border border-white/20 text-white placeholder-white/50'
              }
            `}
          />
          
          {/* Suffix (e.g., %, DA) - positioned right after the value */}
          {suffix && inputValueStr && !inputValueStr.includes(suffix) && (
            <span 
              className={`absolute pointer-events-none ${isLight ? 'text-slate-600' : 'text-white'}`}
              style={{ 
                left: `calc(1rem + ${inputValueStr.length * 0.6}ch + 4px)`,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '16px',
                lineHeight: '1'
              }}
            >
              {suffix}
            </span>
          )}
          
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className="absolute end-3 top-1/2 transform -translate-y-1/2"
          >
            <ChevronDown 
              className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''} ${isLight ? 'text-slate-500' : 'text-white/60'}`} 
            />
          </button>
          {/* Green indicator when creating new value */}
          {isCreatingNew && (
            <div className={`absolute left-0 -bottom-5 flex items-center gap-1.5 text-xs ${isLight ? 'text-emerald-700' : 'text-green-400'}`}>
              <Plus size={12} />
              <span>{createNewPrefix} <strong>{multiple && multiTypingSegment ? multiTypingSegment : inputValueStr}</strong></span>
            </div>
          )}
        </div>
      ) : (
        /* Non-Creatable: Select Button - Click to open */
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            w-full min-w-0 max-w-full box-border rounded-xl px-4 py-2.5
            focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all
            flex items-center justify-between gap-2
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            ${
              isLight
                ? `border border-slate-300 bg-white text-slate-900 ${!disabled ? 'hover:bg-slate-50' : ''}`
                : `border border-white/20 bg-white/10 text-white ${!disabled ? 'hover:bg-blue-500/10' : ''}`
            }
            ${!selectedOption && !multiple ? (isLight ? 'text-slate-400' : 'text-white/50') : ''}
            ${multiple && selectedValues.length === 0 ? (isLight ? 'text-slate-400' : 'text-white/50') : ''}
          `}
        >
          <div className="flex min-w-0 flex-1 items-center justify-start gap-2 overflow-hidden text-start">
            {displayIcon && <span className="flex-shrink-0">{displayIcon}</span>}
            <span style={displayColor ? { color: displayColor } : undefined} className="min-w-0 flex-1 truncate text-start" title={String(displayText ?? '')}>{displayText}</span>
          </div>
          <ChevronDown 
            className={`w-5 h-5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} ${isLight ? 'text-slate-500' : 'text-white'}`} 
          />
        </button>
      )}

      {/* Dropdown Menu - GPS Style (Portal to body) */}
      {isOpen && ReactDOM.createPortal(
        <div 
          ref={dropdownRef}
          className={`rounded-xl overflow-hidden custom-select-dropdown-portal qawaem-portal-dropdown ${qawaem ? 'ring-0' : isLight ? 'ring-1 ring-slate-200/80' : 'shadow-2xl'}`}
          style={dropdownPortalStyle}
        >
          {/* Search — non-creatable selects only (creatable filters via main input) */}
          {searchable && !isCreatable && (
            <div className="p-2 pb-0">
              <div className="relative">
                <Search className={`absolute start-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isLight ? 'text-slate-400' : 'text-white/40'}`} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder}
                  autoComplete={autoComplete}
                  className={`w-full rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 transition-all ${
                    isLight
                      ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:ring-blue-500/35'
                      : 'bg-white/10 border border-white/20 text-white placeholder-white/40 focus:ring-blue-500/50'
                  }`}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}
          
          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            <div className="p-2">
              {multiple && customSelectedOnly.length > 0 && (
                <div className={`mb-2 pb-2 border-b ${isLight ? 'border-slate-200' : 'border-white/10'}`}>
                  {customSelectedOnly.map((sv) => {
                    const selKey = String(sv);
                    return (
                      <button
                        key={`custom-sel-${selKey}`}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleToggleMulti(sv)}
                        className={`mb-1 w-full rounded-xl px-4 py-2.5 text-start text-sm font-semibold flex items-center gap-2 last:mb-0 ${
                          isLight
                            ? 'bg-emerald-50 text-emerald-900 hover:bg-emerald-100'
                            : 'bg-green-500/15 text-white hover:bg-green-500/25'
                        }`}
                      >
                        <CustomSelectMultiCheck
                          checked
                          color={multipleCheckColor}
                          size={20}
                          isLight={isLight}
                        />
                        <span className="min-w-0 flex-1 truncate">{labelForSelectValue(sv, options)}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Create new option - Prominent green button at the TOP when creating new */}
              {isCreatingNew && (
                <div className={`mb-2 pb-2 border-b ${isLight ? 'border-slate-200' : 'border-white/10'}`}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const rawPick =
                        multiple && multiFilterActive
                          ? getMultiCreatableRaw()
                          : isCreatable && !autoSelect
                            ? inputValueRef.current
                            : inputValueStr;
                      if (multiple) {
                        handleAddMultiCustom(rawPick);
                      } else {
                        onChange(
                          typeof rawPick === 'object' && rawPick != null && 'value' in rawPick
                            ? rawPick.value
                            : String(rawPick ?? '').trim()
                        );
                        setIsOpen(false);
                      }
                    }}
                    className={`w-full px-4 py-3 rounded-xl text-start transition-all flex items-center gap-3 group border ${
                      isLight
                        ? 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200/80'
                        : 'bg-green-500/20 hover:bg-green-500/30 border-green-500/40'
                    }`}
                  >
                    <div
                      className={`p-1.5 rounded-lg transition-all ${
                        isLight ? 'bg-emerald-100 group-hover:bg-emerald-200' : 'bg-green-500/30 group-hover:bg-green-500/40'
                      }`}
                    >
                      <Plus size={16} className={isLight ? 'text-emerald-700' : 'text-green-300'} />
                    </div>
                    <div>
                      <div className={`font-semibold text-sm ${isLight ? 'text-emerald-900' : 'text-green-300'}`}>{createLabel}</div>
                      <div className={`text-xs mt-0.5 ${isLight ? 'text-emerald-700/80' : 'text-green-200/70'}`}>
                        {multiple && multiTypingSegment ? multiTypingSegment : inputValueStr}
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {filteredOptions.length === 0 && !isCreatingNew ? (
                <div className={`px-4 py-3 text-center text-sm ${isLight ? 'text-slate-500' : 'text-white/50'}`}>
                  {filterTerm ? noResultsText : noOptionsText}
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = multiple
                    ? selectedValues.some((v) => String(v) === String(option.value))
                    : String(valueKey) === String(option.value);
                  const isDisabled = Boolean(option.disabled);
                  const showPremiumBadge = Boolean(option.premium);
                  const canDelete = onDeleteOption && option.isDefault === false;
                  const optKey =
                    option.value !== '' && option.value != null
                      ? String(option.value)
                      : `__opt__${option.label}`;
                  
                  return (
                    <div
                      key={optKey}
                      className="relative group/option"
                    >
                  <button
                    type="button"
                    data-testid={dataTestId ? `${dataTestId}-option-${optKey}` : undefined}
                    onMouseDown={(e) => {
                      if (!isDisabled) e.preventDefault();
                    }}
                    onClick={() => {
                      if (isDisabled) return;
                      handleSelect(option.value);
                    }}
                    tabIndex={isDisabled ? -1 : 0}
                    aria-disabled={isDisabled}
                    className={`w-full text-start px-4 py-2.5 text-sm font-medium rounded-xl flex items-center gap-2 ${isDisabled ? (isLight ? 'text-slate-400 cursor-not-allowed opacity-[0.72] hover:bg-transparent' : 'text-white/50 cursor-not-allowed opacity-[0.72] hover:bg-transparent') : isLight ? 'transition-colors text-slate-800 hover:bg-slate-100' : 'transition-colors text-white/90 hover:bg-blue-500/20'} ${isSelected && !isDisabled ? (isLight ? 'font-semibold bg-blue-50 text-blue-900' : 'font-semibold bg-blue-500/15') : ''} ${canDelete ? 'pe-10' : ''}`}
                  >
                        {multiple && (
                          <CustomSelectMultiCheck
                            checked={isSelected}
                            color={multipleCheckColor}
                            size={20}
                            isLight={isLight}
                          />
                        )}
                        {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                        <span className="flex-1 truncate min-w-0">{option.label}</span>
                        {showPremiumBadge && (
                          <span
                            className={`flex-shrink-0 inline-flex items-center gap-1 pl-2 border-l ${isLight ? 'border-slate-200' : 'border-white/15'}`}
                            title={t('common.premium')}
                          >
                            <Crown size={14} strokeWidth={2} className="text-amber-400" aria-hidden />
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-400/95">
                              {t('common.premium')}
                            </span>
                          </span>
                        )}
                      </button>
                      
                      {/* X button for user-created options */}
                      {canDelete && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteOption(option.value);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-red-500/30 transition-colors opacity-0 group-hover/option:opacity-100"
                          title={t('common.delete_item', { item: option.label, defaultValue: `حذف "${option.label}"` })}
                        >
                          <X size={14} className={isLight ? 'text-slate-400 hover:text-red-600' : 'text-white/50 hover:text-red-300'} />
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomSelect;

